import Checkbox from '@components/v2/form/Checkbox/Checkbox';
import Button from '@components/v2/ui/Button/Button';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Grid } from '@mui/material';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import LangEnum from '../../lang/LangEnum';
import { ClarityEvents } from '../../lib/clarity/types/clarityEvents.types';
import { captureEvent, withEvent } from '../../lib/clarity/utils/captureEvent.utils';
import useOrderBookStore from '../../stores/OrderBook/orderBook.store';
import { OrderBookStateEnum } from '../../stores/OrderBook/orderBook.types';
import { ActiveOrderTestIds } from '../../utils/testIds/ActiveOrder.testIds';
import { useApplicationContext } from '../context/ApplicationContext/ApplicationContext';
import { useNetworkContext } from '../offline/contexts/network/network.context';
import { useBlockOrderWithoutCustomer } from '../orders/hooks/useBlockOrderWithoutCustomer';
import useProductDetailsActions from '../products/products-details-v2/hooks/useProductsDetailsActions';
import { useTranslation } from '../translation/TranslationContext';
import OrderCustomerAutocomplete from './OrderCustomerAutocomplete';
import useOrderBook from './hooks/useOrderBook';
import { useOrderBookStyles } from './styles/OrderBookStyles';

const CreateOrderFooter: React.FC = () => {
  const { translate } = useTranslation();
  const { isOnline } = useNetworkContext();
  const [customer, createOrder, handleChangeOrderBookTimeline, loadingOrder, loadingOrderItems] =
    useOrderBookStore(
      useShallow((state) => [
        state.customer,
        state.createOrder,
        state.handleChangeOrderBookTimeline,
        state.loadingOrder,
        state.loadingOrderItems,
      ]),
    );
  const { verifyProductDetailsModalOpenedByOrderBook } = useProductDetailsActions();
  const { allOrders } = useOrderBook();
  const [isSuggested, setIsSuggested] = useState(false);
  const { shouldBlockWithoutCustomer } = useBlockOrderWithoutCustomer(isSuggested);
  const { isEnableSuggestedOrder, sellerCanCreateOrChangeSuggestedOrder } = useFeatureFlag();
  const history = useHistory();
  const { isStore, team, isSeller, isBrand } = useApplicationContext();
  const isLoading = loadingOrder || loadingOrderItems;
  const emptyOrders = allOrders?.length === 0;
  const { classes } = useOrderBookStyles();

  const userCanCreateSuggested = isSeller ? sellerCanCreateOrChangeSuggestedOrder : isBrand;

  const handleCreateOrder = () => {
    createOrder({
      history,
      team,
      customerId: customer?.id,
      translate,
      onSuccess: (orderId: string) => {
        captureEvent(ClarityEvents.ORDER_SCREEN_ORDER_CREATED);
        verifyProductDetailsModalOpenedByOrderBook(orderId);
      },
      isSuggested,
    });
  };

  const setSuggestedOrder = () => {
    setIsSuggested(!isSuggested);
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <Grid container spacing={1}>
          <Grid size="grow">
            <Typography variant="overline" color="textSecondary" gutterBottom>
              {translate(LangEnum.ORDER_BOOK_CREATE_NEW_ORDER)}
            </Typography>
          </Grid>
          <Grid size={12}>
            <OrderCustomerAutocomplete
              disabled={isStore}
              disableClearable={false}
              isSuggested={isSuggested}
            />
          </Grid>
          {isOnline && isEnableSuggestedOrder && userCanCreateSuggested && (
            <Grid className={classes.suggestionCheckboxContainer}>
              <Checkbox
                size="medium"
                name="isAutomatic"
                testId={ActiveOrderTestIds.CREATE_AS_SUGGESTION_CHECKBOX}
                label={translate(LangEnum.GLOBAL_CREATE_AS_SUGGESTION)}
                onChange={setSuggestedOrder}
              />
            </Grid>
          )}
        </Grid>
      </Grid>
      <Grid className={classes.item}>
        <Grid
          container
          spacing={2}
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Grid size={4}>
            <Button
              fullWidth
              size="large"
              color="secondary"
              variant="contained"
              testId={ActiveOrderTestIds.ORDER_BOOK_GO_BACK}
              onClick={() =>
                handleChangeOrderBookTimeline(
                  emptyOrders ? OrderBookStateEnum.INITIAL_STATE : OrderBookStateEnum.LIST_ORDERS,
                )
              }
              sx={(theme) => ({
                borderRadius: theme.shape.borderRadiusFull,
              })}
            >
              {translate(LangEnum.GLOBAL_GO_BACK)}
            </Button>
          </Grid>

          <Grid size={8}>
            <Tooltip
              TooltipContent={
                shouldBlockWithoutCustomer
                  ? translate(LangEnum.ACTIVE_ORDER_CREATE_ORDER_WITHOUT_CUSTOMER_TOOLTIP)
                  : undefined
              }
            >
              <Button
                fullWidth
                size="large"
                testId={ActiveOrderTestIds.ORDER_BOOK_CREATE_ORDER_WITHOUT_CUSTOMER}
                color="primary"
                disabled={isLoading || shouldBlockWithoutCustomer}
                variant="contained"
                loading={isLoading}
                onClick={withEvent(
                  ClarityEvents.ORDER_SCREEN_CLICKED_CREATE_ORDER,
                  handleCreateOrder,
                )}
                sx={(theme) => ({
                  borderRadius: theme.shape.borderRadiusFull,
                })}
              >
                {translate(LangEnum.ACTIVE_ORDER_CREATE_ORDER_WITHOUT_CUSTOMER_BUTTON)}
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CreateOrderFooter;
